import React, { useEffect, useState } from "react";
import { MetaTags } from "react-meta-tags";
import sx from "../components/Search/Search.module.scss";
import SearchBar from "../components/medium/SearchBar";
import { getBlogsAPI_v2 } from "../api/GetBlogs";
import { useHistory, useParams } from "react-router-dom";
import { getPostsAPI_v2 } from "../api/GetPosts";
import SortBy from "../components/mini/sortBy";
import Loader from "../components/mini/Loader";
import ArticleCard from "../components/medium/ArticleCard";
import Paginator from "../components/medium/Paginator";
import NoData from "../components/Search/NoData";
import AuthorCard from "../components/medium/AuthorCard";
import { useAuthStore } from "../store/AuthStore";

const getSortingFilters = (sel, isAuth) => {
  const keys = {
    popular: { name: "Most Popular First", show: sel !== "Authors" },
    recent: { name: "Most Recent First", show: sel !== "Authors" },
    following: { name: "Following", show: sel === "Blogs" },
    published: { name: "Published By me", show: false },
    personal: { name: "Personal", show: false },
  };

  if (!isAuth) delete keys.following;

  return Object.entries(keys)
    .map((e) => ({ key: e[0], ...e[1] }))
    .filter((s) => s.show);
};

const Search = () => {
  const history = useHistory();
  const { page, type, value, keywordId } = useParams();
  const { isLoggedIn } = useAuthStore();

  const [search, setSearch] = useState(value || "");
  const [filter, setFilter] = useState("recent");

  const [postData, setPostData] = useState([]);
  const [blogData, setBlogData] = useState([]);
  const [authorData, setAuthorData] = useState([]);

  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);

  //@tanstack/react-query Over complicates things and is not worth it for this page
  const fetchPosts = async (isSuggestion, params) => {
    if (!isSuggestion) setIsLoading(true);
    await getPostsAPI_v2(params)
      .then(({ data }) => {
        if (!isSuggestion) {
          setPostData(data.data.posts);
          setCount(data.data.count);
        } else {
          setSuggestions(data.data.posts);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const fetchBlogs = async (isSuggestion, params) => {
    if (!isSuggestion) setIsLoading(true);
    await getBlogsAPI_v2(params)
      .then(({ data }) => {
        if (!isSuggestion) {
          setBlogData(data.data.blogs);
          setCount(data.data.count);
        } else {
          setSuggestions(data.data.blogs);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const fetchAuthors = async (isSuggestion, params) => {
    if (!isSuggestion) setIsLoading(true);
    await getBlogsAPI_v2(params)
      .then(({ data }) => {
        if (!isSuggestion) {
          setAuthorData(data.data.blogs);
          setCount(data.data.count);
        } else {
          setSuggestions(
            data.data.blogs.map(({ author }) => ({
              imgUrl: author.profileImage,
              title: `${author.firstName} ${author.lastName ?? ""}`,
            }))
          );
        }
      })
      .finally(() => setIsLoading(false));
  };

  // Initial Fetch
  useEffect(() => {
    setSuggestions([]);

    const initialParam = {
      size: 10,
      page: page,
      category: filter,
      search: value || "",
    };

    const popularKeysParam = {
      size: 10,
      page: page,
      tagIds: keywordId,
    };

    if (type === "posts") {
      fetchPosts(false, keywordId ? popularKeysParam : initialParam);
    }
    if (type === "blogs") {
      fetchBlogs(false, initialParam);
    }
    if (type === "authors") {
      fetchAuthors(false, initialParam);
    }
  }, [page, type, value, filter, keywordId]);

  const handleTypeChange = (newType) => {
    setSearch("");
    history.push(`/search/1/${newType}`);
  };

  const handlePagination = (newPage) => {
    keywordId
      ? history.push(`/search/${newPage}/${type}/${search}/${keywordId}`)
      : history.push(`/search/${newPage}/${type}/${search}`);
  };

  const handleFilterChange = (filter) => {
    setFilter(filter);
  };

  const onSearchClick = () => {
    history.push(`/search/1/${type}/${search}`);
  };

  const onSuggestionClick = async (val) => {
    const params = {
      size: 10,
      page: 1,
      category: filter,
      search: val,
      author: val,
    };

    if (type === "posts") {
      delete params.author;
      fetchPosts(false, params);
    }
    if (type === "blogs") {
      delete params.author;
      fetchBlogs(false, params);
    }
    if (type === "authors") {
      delete params.search;
      fetchAuthors(false, params);
    }
  };

  const onClear = () => {
    history.push(`/search/1/${type}`);
  };

  const onSearchChange = async (val) => {
    setSearch(val);

    if (val === "") {
      setSuggestions([]);
      onClear();
    }

    if (val.length > 2) {
      const params = {
        size: 10,
        page: 1,
        category: filter,
        search: val,
        author: val,
      };

      if (type === "posts") {
        delete params.author;
        fetchPosts(true, params);
      }
      if (type === "blogs") {
        delete params.author;
        fetchBlogs(true, params);
      }
      if (type === "authors") {
        delete params.search;
        fetchAuthors(true, params);
      }
    }
  };

  return (
    <>
      <MetaTags>
        <title>Physician Blogs - Search</title>
      </MetaTags>

      <SearchBar
        selDrop={type}
        setSelDrop={handleTypeChange}
        searchValue={search}
        onSearchChange={onSearchChange}
        onSuggestionClick={onSuggestionClick}
        onSearchClick={onSearchClick}
        onClear={onClear}
        suggestions={suggestions}
      />

      <div className={sx.root}>
        <div className={sx.main}>
          <div className={sx.header}>
            {isLoading ? (
              <div className={sx.results}>Searching ...</div>
            ) : (
              count > 0 && (
                <div className={sx.results}>Search Results ({count})</div>
              )
            )}
            <div className={sx.filter}>
              <p className={sx.title}>Sort By:</p>
              <SortBy
                currentValue={filter}
                fields={getSortingFilters(type, isLoggedIn)}
                onSelect={handleFilterChange}
              />
            </div>
          </div>

          <div className={sx.content}>
            {/* POSTS */}
            {type === "posts" &&
              (isLoading ? (
                <Loader />
              ) : (
                <>
                  {postData.length > 0 ? (
                    <>
                      {postData.map((post) => (
                        <ArticleCard
                          key={post.id}
                          post={post}
                          isBookmarkTab={false}
                        />
                      ))}
                      {count > 10 && (
                        <Paginator
                          count={count}
                          currentPage={Number(page)}
                          pageChange={handlePagination}
                        />
                      )}
                    </>
                  ) : (
                    <NoData />
                  )}
                </>
              ))}

            {/* BLOGS */}
            {type === "blogs" &&
              (isLoading ? (
                <Loader />
              ) : (
                <>
                  {blogData.length > 0 ? (
                    <>
                      {blogData.map((blog) => (
                        <AuthorCard key={blog.id} blog={blog} />
                      ))}
                      {count > 10 && (
                        <Paginator
                          count={count}
                          currentPage={Number(page)}
                          pageChange={handlePagination}
                        />
                      )}
                    </>
                  ) : (
                    <NoData />
                  )}
                </>
              ))}

            {/* AUTHORS */}
            {type === "authors" &&
              (isLoading ? (
                <Loader />
              ) : (
                <>
                  {authorData.length > 0 ? (
                    <>
                      {authorData.map((blog) => (
                        <AuthorCard key={blog.id} blog={blog} />
                      ))}
                      {count > 10 && (
                        <Paginator
                          count={count}
                          currentPage={Number(page)}
                          pageChange={handlePagination}
                        />
                      )}
                    </>
                  ) : (
                    <NoData />
                  )}
                </>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Search;
